import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import { replaceEmptyStringWithUndefined } from "@/shared/lib/object";
import {
  customZodResolver,
  useSubscribeFromValues,
} from "@/shared/lib/react-hook-form";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseBreadCrumb } from "@/shared/ui/base-bread-crumb";
import { BaseMessageCard } from "@/shared/ui/base-message-card";
import {
  Box,
  Container,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import type React from "react";
import { useState } from "react";
import { type DeepPartial, useForm } from "react-hook-form";
import type { ShipperCargoCreatePageProps } from "../../model";
import {
  type CreateOfferCargoFormSchemaType,
  createOfferCargoFormSchema,
} from "../../model/validation";
import { CargoForm } from "./forms/cargo-form";
import { DeliveryProposalForm } from "./forms/delivery-proposal-form";
import { DestinationsForm } from "./forms/destinations-form";
import { MemoForm } from "./forms/memo-form";
import { PaymentForm } from "./forms/payment-form";
import { ScheduleForm } from "./forms/schedule-form";
import { VehicleForm } from "./forms/vehicle-form";

export const CargoCreateInProgress: React.FC<ShipperCargoCreatePageProps> = ({
  tab,
  isEasyForm,
  defaultInputValues,
  onTabChange,
  onConfirmClick,
  onInputValuesUpdate,
}) => {
  const links = [
    { href: "/cargos", label: "マイ荷物" },
    { href: "/cargos/create", label: "荷物の新規登録" },
  ];

  // FileオブジェクトをLocalStorageにシリアライズできないので、react-hook-formとは別に管理
  const [destinationFiles, setDestinationFiles] = useState<File[]>();

  const { handleSubmit, control, watch, getValues, formState, setValue } =
    useForm<CreateOfferCargoFormSchemaType>({
      resolver: customZodResolver(createOfferCargoFormSchema),
      mode: "onChange",
      defaultValues: defaultInputValues ?? {
        highwayFee: 1,
        destinations: [
          {
            hasLoading: true,
            hasUnloading: false,
            deadlineType: "till" as const,
            deadlineTime: undefined,
            deadlineTimeEnd: undefined,
            zipCode: undefined,
            prefecture: undefined,
            city: undefined,
            addressLine: undefined,
            buildingName: undefined,
            chargePersonName: undefined,
            contactInfo: undefined,
            taskDetail: undefined,
            memo: undefined,
          },
          {
            hasLoading: false,
            hasUnloading: true,
            deadlineType: "till" as const,
            deadlineTime: undefined,
            deadlineTimeEnd: undefined,
            zipCode: undefined,
            prefecture: undefined,
            city: undefined,
            addressLine: undefined,
            buildingName: undefined,
            chargePersonName: undefined,
            contactInfo: undefined,
            taskDetail: undefined,
            memo: undefined,
          },
        ],
      },
    });
  const handleInputValuesUpdate = (
    values: DeepPartial<CreateOfferCargoFormSchemaType>,
  ) => {
    onInputValuesUpdate(replaceEmptyStringWithUndefined(values));
  };
  useSubscribeFromValues(handleInputValuesUpdate, watch, true);

  const hasErrors = Object.keys(formState.errors).length > 0;

  // 詳細入力タブでのみ表示されるフィールドにバリデーションエラーがあるかチェック
  const detailedOnlyFields = [
    "highwaySection",
    "fuelSurcharge",
    "cargoHandlingFee",
    "othersFee",
    "adjustmentFee",
    "paymentDate",
    "basicFeeMemo",
    "name",
    "addressLine",
    "buildingName",
    "chargePersonName",
    "contactInfo",
    "taskDetail",
    "vehicleEquipmentMemo",
    "memo",
  ];

  // 詳細入力タブでのみ表示されるフィールドにバリデーションエラーがあるかチェック
  const hasDetailedOnlyErrors = (() => {
    // トップレベルの詳細入力タブのみのフィールドにエラーがあるかチェック
    const hasTopLevelDetailedOnlyErrors = detailedOnlyFields.some((field) =>
      Object.keys(formState.errors).includes(field),
    );

    // 配送先の中の詳細入力タブのみのフィールドにエラーがあるかチェック
    const hasNestedDetailedOnlyErrors =
      formState.errors.destinations &&
      Array.isArray(formState.errors.destinations) &&
      formState.errors.destinations.some(
        (destError: Record<string, unknown>) => {
          if (!destError) return false;
          return Object.keys(destError).some((field) =>
            [
              "zipCode",
              "addressLine",
              "buildingName",
              "chargePersonName",
              "contactInfo",
              "taskDetail",
              "memo",
            ].includes(field),
          );
        },
      );

    return Boolean(
      hasTopLevelDetailedOnlyErrors || hasNestedDetailedOnlyErrors,
    );
  })();

  return (
    <Box px={{ xs: 2, sm: 4 }}>
      <ScrollToTop />
      <BaseBreadCrumb links={links} sx={{ mb: 3 }} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          荷物の新規登録
        </Typography>
      </Box>

      <Container maxWidth="md" sx={{ px: 0 }}>
        <Tabs
          centered
          variant="fullWidth"
          textColor="primary"
          aria-label="cargo register form tabs"
          value={tab}
          onChange={(_, value) => onTabChange(value)}
        >
          <Tab label="かんたん登録" value={"easy"} />
          <Tab
            value="normal"
            // ← labelにTooltipを仕込み、吹き出しが常時表示されるように制御
            label={
              <Tooltip
                title="正しく入力されていない項目があります"
                placement="top"
                arrow
                // エラーがあるときだけ常時表示
                open={isEasyForm && hasDetailedOnlyErrors}
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "#FEF4F2",
                      border: "1px solid #EA583B",
                      color: "#EA583B",
                      fontSize: 14,
                    },
                  },
                  arrow: {
                    sx: {
                      color: "#FEF4F2",
                      ":before": {
                        border: "1px solid #EA583B",
                      },
                    },
                  },
                }}
              >
                <Box component="span">しっかり登録</Box>
              </Tooltip>
            }
          />
        </Tabs>
      </Container>

      <Box
        sx={{
          mx: { xs: -2, sm: 0 },
        }}
      >
        <form
          onSubmit={handleSubmit((data) => {
            // requestProposalがtrueの場合、shippingFeeを0に設定
            const finalData = data.requestProposal
              ? { ...data, shippingFee: 0 }
              : data;

            onConfirmClick({
              ...finalData,
              stage: "draft",
              destinationFiles,
            });
          })}
        >
          <Box
            display="flex"
            bgcolor="grey.50"
            minHeight="600px"
            flexDirection={"column"}
            flexGrow={1}
          >
            <Container maxWidth="md" sx={{ px: { xs: 0, sm: 2 } }}>
              <Stack spacing={3} sx={{ mt: 4, mb: { xs: 4, sm: 10 }, py: 2 }}>
                <ScheduleForm control={control} />
                <DestinationsForm
                  isEasyForm={isEasyForm}
                  control={control}
                  formState={formState}
                  watch={watch}
                  onDestinationFilesChange={setDestinationFiles} // FileオブジェクトをLocalStorageにシリアライズできないので、react-hook-formとは別に管理
                  onDestinationInsertOrRemove={() => {
                    // useFieldArrayのinsert/removeがwatchを発火させないため、手動で更新
                    handleInputValuesUpdate(getValues());
                  }}
                />
                <PaymentForm
                  isEasyForm={isEasyForm}
                  control={control}
                  setValue={setValue}
                />
                <CargoForm isEasyForm={isEasyForm} control={control} />
                <VehicleForm isEasyForm={isEasyForm} control={control} />
                {!isEasyForm && <MemoForm control={control} />}
                <DeliveryProposalForm control={control} />
              </Stack>
            </Container>
          </Box>
          <Stack spacing={2} p={2} alignItems="center" justifyContent="center">
            {hasErrors && (
              <BaseMessageCard
                status="error"
                message={
                  "正しく入力されていない項目があります。再度ご確認ください。"
                }
              />
            )}
            <BaseButton
              type="submit"
              variant="contained"
              color="primary"
              sx={{ my: 2, width: "240px" }}
            >
              確認する
            </BaseButton>
          </Stack>
        </form>
      </Box>
    </Box>
  );
};
